import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">Artigo não encontrado</h1>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
            Voltar ao início
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <article className="pt-20 md:pt-24">
        <div className="px-6 md:px-10 py-10 md:py-14">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-foreground-700 transition-colors cursor-pointer">
                Início
              </Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <span className="text-foreground-600">Artigos</span>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <span className="text-foreground-800 truncate max-w-[200px]">{article.title}</span>
            </nav>

            <div className="mb-8">
              <span className="inline-block px-3 py-1 text-[11px] font-medium text-primary-600 bg-primary-50 rounded-full mb-4">
                {article.readTime} de leitura
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-6 leading-relaxed">
                {article.metaDescription}
              </p>
              <div className="flex items-center gap-4 text-xs text-foreground-400">
                <span>Publicado em {article.date}</span>
                <span>•</span>
                <span>Hoobuy Sheet</span>
              </div>
            </div>

            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-[300px] md:h-[400px] object-cover object-top"
              />
            </div>

            <div className="prose-custom">
              {article.sections.map((section, idx) => (
                <div key={idx} className="mb-10">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    {section.heading}
                  </h2>

                  {section.type === "list" ? (
                    <ul className="space-y-3">
                      {(Array.isArray(section.content) ? section.content : []).map((item, i) => (
                        <li key={i} className="flex gap-3 text-sm text-foreground-700 leading-relaxed">
                          <span className="w-6 h-6 rounded-full bg-primary-50 flex-shrink-0 flex items-center justify-center mt-0.5">
                            <span className="text-[11px] font-semibold text-primary-600">{i + 1}</span>
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : section.type === "tip" ? (
                    <div className="bg-accent-50 border border-accent-200/50 rounded-xl p-5 md:p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-full bg-accent-200 flex items-center justify-center">
                          <i className="ri-lightbulb-line text-accent-700 text-sm"></i>
                        </div>
                        <span className="text-xs font-semibold text-accent-700 uppercase tracking-wider">Dica Importante</span>
                      </div>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        {typeof section.content === "string" ? section.content : ""}
                      </p>
                    </div>
                  ) : typeof section.content === "string" ? (
                    <p className="text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                      {section.content}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="border-t border-background-200/60 pt-8 mt-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Voltar para o Início
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all cursor-pointer whitespace-nowrap"
                >
                  Acessar Hoobuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>

            <div className="mt-10 p-5 md:p-6 bg-background-100/70 rounded-xl border border-background-200/60">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
                Tags
              </p>
              <div className="flex flex-wrap gap-2">
                {article.keywords.split(", ").map((kw) => (
                  <span
                    key={kw}
                    className="px-3 py-1 text-[11px] font-medium text-foreground-600 bg-background-200/70 rounded-full whitespace-nowrap"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </div>

            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": article.title,
                "description": article.metaDescription,
                "image": article.image,
                "author": {
                  "@type": "Organization",
                  "name": "Hoobuy Sheet"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Hoobuy Sheet",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/879fae149d7d175a673508cd1be74143.jpeg"
                  }
                },
                "datePublished": article.date,
                "dateModified": article.date,
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `https://hoobuy.autos/artigo/${article.slug}`
                },
                "inLanguage": "pt-BR",
                "keywords": article.keywords,
                "isAccessibleForFree": true
              })}
            </script>

            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Início",
                    "item": "https://hoobuy.autos/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Artigos",
                    "item": "https://hoobuy.autos/artigo/guia-completo-hoobuy-spreadsheet"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": article.title,
                    "item": `https://hoobuy.autos/artigo/${article.slug}`
                  }
                ]
              })}
            </script>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}